import type { Camera, Matrix4, PerspectiveCamera, Raycaster } from 'three'
import { Plane, Vector3 } from 'three'

import { setRaycasterFromClient } from './dragRaycast'

const FLOOR_NORMAL = new Vector3(0, 1, 0)
const hitScratch = new Vector3()
const posScratch = new Vector3()
const camDirScratch = new Vector3()
const camRightScratch = new Vector3()
const forwardXZScratch = new Vector3()
const rightXZScratch = new Vector3()
const horizontalPlaneScratch = new Plane()

/** Mínimo |dir.y| del rayo para usar el plano horizontal. */
const MIN_HORIZONTAL_RAY_Y = 0.025

/** Coordenada máxima absoluta (mm) en el plano XZ durante arrastre. */
export const MAX_FLOOR_DRAG_COORD_MM = 50_000

export type FloorDragPlaneMode = 'horizontal' | 'camera'

export function isSaneFloorDragPosition(position: Vector3): boolean {
  return (
    Number.isFinite(position.x) &&
    Number.isFinite(position.y) &&
    Number.isFinite(position.z) &&
    Math.abs(position.x) <= MAX_FLOOR_DRAG_COORD_MM &&
    Math.abs(position.z) <= MAX_FLOOR_DRAG_COORD_MM
  )
}

export type FloorDragSession = {
  planeY: number
  planeMode: FloorDragPlaneMode
  /** Offset mundo (XZ) entre el pivote del item y el punto bajo el cursor al iniciar. */
  grabOffset: Vector3
  /** Plano alineado a cámara (solo si planeMode === 'camera'). */
  fallbackPlane: Plane
  initialMatrix: Matrix4
}

function raycastPlaneAtClient(
  raycaster: Raycaster,
  camera: Camera,
  clientX: number,
  clientY: number,
  canvas: HTMLElement,
  plane: Plane,
  target: Vector3,
): boolean {
  setRaycasterFromClient(raycaster, camera, clientX, clientY, canvas)
  return raycaster.ray.intersectPlane(plane, target)
}

function raycastHorizontalAtClient(
  raycaster: Raycaster,
  camera: Camera,
  clientX: number,
  clientY: number,
  canvas: HTMLElement,
  planeY: number,
  target: Vector3,
): boolean {
  setRaycasterFromClient(raycaster, camera, clientX, clientY, canvas)
  if (Math.abs(raycaster.ray.direction.y) < MIN_HORIZONTAL_RAY_Y) return false

  horizontalPlaneScratch.set(FLOOR_NORMAL, -planeY)
  return raycaster.ray.intersectPlane(horizontalPlaneScratch, target)
}

function buildCameraFallbackPlane(camera: Camera, coplanarPoint: Vector3): Plane {
  camera.getWorldDirection(camDirScratch)
  if (Math.abs(camDirScratch.y) < MIN_HORIZONTAL_RAY_Y) {
    camDirScratch.y = MIN_HORIZONTAL_RAY_Y
  }
  camDirScratch.normalize()
  return new Plane().setFromNormalAndCoplanarPoint(camDirScratch, coplanarPoint)
}

function resolveSessionHit(
  session: FloorDragSession,
  raycaster: Raycaster,
  camera: Camera,
  clientX: number,
  clientY: number,
  canvas: HTMLElement,
  target: Vector3,
): boolean {
  if (session.planeMode === 'horizontal') {
    return raycastHorizontalAtClient(
      raycaster,
      camera,
      clientX,
      clientY,
      canvas,
      session.planeY,
      target,
    )
  }
  return raycastPlaneAtClient(
    raycaster,
    camera,
    clientX,
    clientY,
    canvas,
    session.fallbackPlane,
    target,
  )
}

/**
 * Inicia sesión de arrastre: fija el offset entre pivote del item y el punto bajo el cursor.
 * El plano de proyección queda bloqueado durante toda la sesión (evita saltos entre planos).
 */
export function beginFloorDragSession(
  raycaster: Raycaster,
  camera: Camera,
  canvas: HTMLElement,
  clientX: number,
  clientY: number,
  itemPosition: Vector3,
  itemMatrix: Matrix4,
): FloorDragSession | null {
  const planeY = itemPosition.y
  const fallbackPlane = buildCameraFallbackPlane(camera, itemPosition)

  let planeMode: FloorDragPlaneMode = 'horizontal'
  let hasHit = raycastHorizontalAtClient(
    raycaster,
    camera,
    clientX,
    clientY,
    canvas,
    planeY,
    hitScratch,
  )

  if (!hasHit) {
    planeMode = 'camera'
    hasHit = raycastPlaneAtClient(
      raycaster,
      camera,
      clientX,
      clientY,
      canvas,
      fallbackPlane,
      hitScratch,
    )
  }

  if (!hasHit) return null

  const grabOffset = itemPosition.clone().sub(hitScratch)
  grabOffset.y = 0

  return {
    planeY,
    planeMode,
    grabOffset,
    fallbackPlane,
    initialMatrix: itemMatrix.clone(),
  }
}

/**
 * Posición absoluta del pivote siguiendo el cursor (mantiene el punto de agarre).
 */
export function computeFloorDragPosition(
  session: FloorDragSession,
  raycaster: Raycaster,
  camera: Camera,
  canvas: HTMLElement,
  clientX: number,
  clientY: number,
  _previousPosition?: Vector3 | null,
  target = posScratch,
): Vector3 | null {
  if (
    !resolveSessionHit(session, raycaster, camera, clientX, clientY, canvas, hitScratch)
  ) {
    return null
  }

  target.set(
    hitScratch.x + session.grabOffset.x,
    session.planeY,
    hitScratch.z + session.grabOffset.z,
  )

  if (!isSaneFloorDragPosition(target)) return null
  return target
}

/** Escala mm/píxel en el plano y = planeY (fallback extremo). */
export function estimateWorldMmPerPixel(
  camera: PerspectiveCamera,
  canvas: HTMLElement,
  planeY: number,
  anchor: Vector3,
): number {
  const rect = canvas.getBoundingClientRect()
  if (rect.height <= 0) return 1

  const distance = Math.max(
    400,
    Math.hypot(
      camera.position.x - anchor.x,
      camera.position.y - planeY,
      camera.position.z - anchor.z,
    ),
  )
  const vFovRad = (camera.fov * Math.PI) / 180
  return (2 * Math.tan(vFovRad / 2) * distance) / rect.height
}

/**
 * Fallback incremental alineado a cámara (mismo plano XZ, sin cambiar de espacio).
 */
export function computeCameraAlignedScreenDelta(
  camera: PerspectiveCamera,
  canvas: HTMLElement,
  fromClient: { x: number; y: number },
  toClient: { x: number; y: number },
  planeY: number,
  anchor: Vector3,
  target = new Vector3(),
): Vector3 {
  const mmPerPx = estimateWorldMmPerPixel(camera, canvas, planeY, anchor)
  const dxPx = toClient.x - fromClient.x
  const dyPx = toClient.y - fromClient.y

  camera.getWorldDirection(camDirScratch)
  forwardXZScratch.set(camDirScratch.x, 0, camDirScratch.z)
  if (forwardXZScratch.lengthSq() < 1e-6) {
    forwardXZScratch.set(0, 0, -1)
  } else {
    forwardXZScratch.normalize()
  }

  camRightScratch.setFromMatrixColumn(camera.matrixWorld, 0).normalize()
  rightXZScratch.set(camRightScratch.x, 0, camRightScratch.z)
  if (rightXZScratch.lengthSq() < 1e-6) {
    rightXZScratch.set(1, 0, 0)
  } else {
    rightXZScratch.normalize()
  }

  target.set(
    rightXZScratch.x * dxPx * mmPerPx + forwardXZScratch.x * -dyPx * mmPerPx,
    0,
    rightXZScratch.z * dxPx * mmPerPx + forwardXZScratch.z * -dyPx * mmPerPx,
  )
  return target
}

export function raycastHorizontalFloorPlane(
  raycaster: Raycaster,
  camera: Camera,
  clientX: number,
  clientY: number,
  canvas: HTMLElement,
  planeY: number,
  anchor?: Vector3,
): { point: Vector3 } | null {
  const fallbackPlane = buildCameraFallbackPlane(
    camera,
    anchor ?? new Vector3(0, planeY, 0),
  )
  if (
    raycastHorizontalAtClient(raycaster, camera, clientX, clientY, canvas, planeY, hitScratch)
  ) {
    return { point: hitScratch.clone() }
  }
  if (
    raycastPlaneAtClient(
      raycaster,
      camera,
      clientX,
      clientY,
      canvas,
      fallbackPlane,
      hitScratch,
    )
  ) {
    return { point: hitScratch.clone() }
  }
  return null
}
